/**
 * 🔴 O QUE A CARTEIRA TEM, EM NÚMERO — e a frase do roteamento que não mente.
 *
 * ── O caso (#458, achado em 29/09/2026 no smoke do item U2)
 *
 * A tela prometia, em letra de subtítulo:
 * *"Novas oportunidades do cliente roteiam automaticamente pro vendedor dono."*
 *
 * 🔴 **Medido no mesmo dia: 500 dos 513 clientes não têm dono.** A promessa
 * valia para **13**. ⚠️ Não era mentira de quem escreveu — era uma frase escrita
 * quando a carteira ainda ia ser preenchida, e que **nunca foi revisada depois
 * que o número apareceu na tela.**
 *
 * ✅ **A régua que isto aplica:** *"a etiqueta diz o FATO, não a intenção"*. Uma
 * frase que promete um comportamento **diz para quantos ele vale** — senão quem
 * a lê conclui que a carteira está funcionando e para de atribuir.
 *
 * ⚠️ **E o tom importa tanto quanto o número.** Os 500 sem dono **não são um
 * alarme**: são o trabalho normal, que a equipe faz conforme toca cada cliente.
 * 🔴 Transformar isso num aviso vermelho permanente ensinaria a ignorá-lo — e
 * aí o dia em que algo estiver mesmo errado ninguém olha.
 */

export interface ResumoDaCarteira {
  /** Clientes na carteira (só quem tem a etiqueta Cliente). */
  total: number;
  /** Quantos têm vendedor dono — é para estes que o roteamento vale. */
  comDono: number;
  /** Quantos ainda não têm. ⚠️ Trabalho pendente, ❌ não defeito. */
  semDono: number;
}

/** A chave que o recorte usa para "sem vendedor" — uma fonte só. */
export const CHAVE_SEM_DONO = 'sem';

export function resumoDaCarteira(contagens: Record<string, number>): ResumoDaCarteira {
  const total = Object.values(contagens).reduce((a, n) => a + n, 0);
  const semDono = contagens[CHAVE_SEM_DONO] ?? 0;
  return { total, comDono: Math.max(0, total - semDono), semDono };
}

/**
 * A frase do roteamento, com o número dentro.
 *
 * 🔴 **Ela diz PARA QUANTOS vale** — e, quando há pendentes, diz o que fazer
 * sem transformar o número num alarme.
 *
 * ⚠️ **Carteira vazia não ganha frase de erro:** sem cliente nenhum, não há
 * promessa a qualificar, e inventar um aviso seria ruído.
 */
export function fraseDoRoteamento(r: ResumoDaCarteira, soComContrato = false): string {
  if (r.total === 0) return 'Novas oportunidades do cliente roteiam automaticamente pro vendedor dono.';
  /**
   * 🔴 19/10/2026 (AJUSTES-CARTEIRA, item A): com "Só com contrato" ligada, os números contam SÓ quem tem contrato, e a
   * frase antiga ("hoje isso vale para 0 de 48 clientes") fazia a carteira inteira parecer ter 48. ✅ A frase passa a dizer
   * de qual conjunto fala. Texto literal do handoff.
   */
  if (soComContrato) {
    const dos = r.total === 1 ? 'Do 1 cliente com contrato' : `Dos ${r.total} clientes com contrato`;
    return (
      `Novas oportunidades roteiam automaticamente pro vendedor dono. ${dos}, ${r.comDono} ` +
      `${r.comDono === 1 ? 'tem' : 'têm'} vendedor dono e ${r.semDono} ${r.semDono === 1 ? 'nasce' : 'nascem'} sem ` +
      `vendedor até alguém atribuir.`
    );
  }
  if (r.semDono === 0) {
    return `Novas oportunidades roteiam automaticamente pro vendedor dono — vale para os ${r.total} clientes da carteira.`;
  }
  return (
    `Novas oportunidades roteiam automaticamente pro vendedor dono — hoje isso vale para ` +
    `${r.comDono} de ${r.total} clientes. Os outros ${r.semDono} ainda não têm dono, e ` +
    `nascem sem vendedor até alguém atribuir.`
  );
}
